import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { AuditLog } from '../../../types/audit-log';
import { getHistoricoDeAlteracoes } from '../../../api/auditLogApi';
import { format } from 'date-fns';

const MAX_VALUE_LENGTH = 120;

/** Serializes a value compactly: arrays and plain values stay on one line. */
const serializeCompact = (value: unknown): string => {
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value !== 'object') return String(value);
    // Compact single-line JSON for arrays and objects
    return JSON.stringify(value);
};

/** Truncates a string if it exceeds MAX_VALUE_LENGTH. */
const truncate = (str: string, max = MAX_VALUE_LENGTH): { text: string; truncated: boolean } => {
    if (str.length <= max) return { text: str, truncated: false };
    return { text: str.slice(0, max), truncated: true };
};

const AuditValue = ({ value }: { value: unknown }) => {
    const [expanded, setExpanded] = useState(false);
    const raw = serializeCompact(value);
    const { text, truncated } = truncate(raw);
    const display = expanded ? raw : text;

    return (
        <span className="auditlog-json-value">
            {display}
            {truncated && !expanded && (
                <>
                    {'… '}
                    <button className="auditlog-expand-btn" onClick={() => setExpanded(true)}>
                        ver mais
                    </button>
                </>
            )}
            {truncated && expanded && (
                <>
                    {' '}
                    <button className="auditlog-expand-btn" onClick={() => setExpanded(false)}>
                        ver menos
                    </button>
                </>
            )}
        </span>
    );
};

const Index = (props: any) => {
    const [data, setData] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const renderAuditPayload = (payload: unknown) => {
        if (payload === null || payload === undefined || payload === '') return '-';

        if (typeof payload !== 'object' || Array.isArray(payload)) {
            return <AuditValue value={payload} />;
        }

        const entries = Object.entries(payload as Record<string, unknown>);
        if (!entries.length) return '-';

        return (
            <div className="auditlog-json-container">
                {entries.map(([key, value]) => (
                    <div key={key} className="auditlog-json-entry">
                        <strong>{key}:</strong>{' '}
                        <AuditValue value={value} />
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        getHistoricoDeAlteracoes()
            .then((res) => {
                setData(res);
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const columns = useMemo(
        () => [
            {
                header: '#',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Usuário',
                accessorFn: (row: any) => {
                    const context = row.context;
                    if (context.impersonatedUser) {
                        return `${context.impersonatedUser.name} (Personificado como ${context.user.email})`;
                    }

                    if (context.user) {
                        return `${context.user.name}`;
                    }

                    return '-';
                },
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Method',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const context = row.context;
                    const actionColors: { [key: string]: string } = {
                        GET: 'bg-primary-subtle text-primary',
                        POST: 'bg-secondary-subtle text-secondary',
                        DELETE: 'bg-danger-subtle text-danger',
                        PUT: 'bg-success-subtle text-success',
                        PATCH: 'bg-warning-subtle text-warning',
                    };
                    const colorClass = actionColors[context.method] || 'bg-soft-secondary';
                    return <span className={`badge ${colorClass}`}>{context.method}</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'IP',
                accessorFn: (row: any) => row.ipv4 || row.ipv6 || '-',
                enableColumnFilter: false,
            },
            {
                header: 'Entity',
                accessorKey: 'entity',
                enableColumnFilter: false,
            },
            {
                header: 'Action',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const action = row.action.toUpperCase();
                    const actionColors: { [key: string]: string } = {
                        INSERT: 'bg-success-subtle text-success',
                        DELETE: 'bg-danger-subtle text-danger',
                        UPDATE: 'bg-secondary-subtle text-secondary',
                    };
                    const colorClass = actionColors[action] || 'bg-info-subtle text-info';
                    return <span className={`badge ${colorClass}`}>{action}</span>;
                },
                enableColumnFilter: false,
            },
            {
                header: 'From',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    return renderAuditPayload(row.from);
                },
                enableColumnFilter: false,
            },
            {
                header: 'To',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    return renderAuditPayload(row.to);
                },
                enableColumnFilter: false,
            },
            {
                header: 'Timestamp',
                accessorFn: (row: any) => format(row.timestamp, 'dd/MM/yyyy HH:mm:ss XXX'),
                enableColumnFilter: false,
            },
        ],
        [],
    );

    document.title = 'Logs de auditoria | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader>
                                    <div className="row g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">Logs de auditoria</h5>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <React.Fragment>
                                        {!loading ? (
                                            <TableContainer
                                                divClass="table-responsive overflow-x-auto w-full"
                                                columns={columns || []}
                                                data={data || []}
                                                isGlobalFilter={true}
                                                tableClass="auditlog-table"
                                                customPageSize={30}
                                                SearchPlaceholder="Procurar..."
                                            />
                                        ) : (
                                            <div className="text-center">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Carregando...</span>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
